import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import QuestionRenderer from "../common/QuestionRenderer";

const formatSubmittedAt = (value) => {
  if (!value) return "Date unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatAnswer = (answer) => {
  if (Array.isArray(answer)) return answer.length ? answer.join(", ") : "No answer";
  if (answer === null || answer === undefined || answer === "") return "No answer";
  if (typeof answer === "object") return JSON.stringify(answer);
  return String(answer);
};

const ParentFeedbackHistory = ({ entries = [], loading = false }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(0);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.25} mb={1}>
        <HistoryRoundedIcon sx={{ color: "#F6AA48", fontSize: 30 }} />
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#292D5B" }}>
            Previous Feedback
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#697089", mt: 0.25 }}>
            Completed Parent/Teacher feedback for the selected student.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} sx={{ color: "#F6AA48" }} />
        </Box>
      ) : entries.length === 0 ? (
        <Typography sx={{ color: "#697089", fontSize: 13, py: 2 }}>
          No completed feedback is available yet.
        </Typography>
      ) : (
        <Box mt={1.5}>
          {entries.map((entry) => {
            const isExpanded = expandedId === entry.id;
            const submittedBy = entry.completed_by?.name || entry.source_label;
            const responses = Array.isArray(entry.responses) ? entry.responses : [];

            return (
              <Box
                key={entry.id}
                sx={{
                  borderTop: "1px solid #E8EAF0",
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <PersonRoundedIcon sx={{ color: "#697089", fontSize: 18 }} />
                      <Typography
                        sx={{
                          color: "#292D5B",
                          fontSize: 14,
                          fontWeight: 800,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {submittedBy}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                      <EventRoundedIcon sx={{ color: "#8A91A5", fontSize: 16 }} />
                      <Typography sx={{ color: "#697089", fontSize: 12 }}>
                        {formatSubmittedAt(entry.submitted_at)}
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip title={isExpanded ? "Hide answers" : "View answers"}>
                    <IconButton
                      size="small"
                      aria-label={isExpanded ? "Hide feedback answers" : "View feedback answers"}
                      aria-expanded={isExpanded}
                      onClick={() => {
                        setExpandedId(isExpanded ? null : entry.id);
                        setExpandedQuestionIndex(0);
                      }}
                      sx={{
                        flexShrink: 0,
                        color: "#292D5B",
                        backgroundColor: "#F3F4F8",
                        "&:hover": { backgroundColor: "#EAECF3" },
                      }}
                    >
                      <ExpandMoreRoundedIcon
                        sx={{
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 180ms ease",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ pt: 1.5, pl: { xs: 0, sm: 3 } }}>
                    {responses.length === 0 ? (
                      <Typography sx={{ color: "#697089", fontSize: 13 }}>
                        Answer details are not available for this older feedback.
                      </Typography>
                    ) : (
                      responses.map((response, index) => {
                        const hasFormOptions =
                          ["radio", "checkbox"].includes(response.type) &&
                          Array.isArray(response.options) &&
                          response.options.length > 0;

                        if (hasFormOptions) {
                          const value = response.type === "checkbox"
                            ? Array.isArray(response.answer) ? response.answer : []
                            : response.answer || "";
                          return (
                            <QuestionRenderer
                              key={`${entry.id}-${response.qid || index}`}
                              question={response}
                              value={value}
                              onChange={() => {}}
                              index={index}
                              expanded={expandedQuestionIndex}
                              onExpandChange={setExpandedQuestionIndex}
                              readOnly
                            />
                          );
                        }

                        return (
                          <Box
                            key={`${entry.id}-${response.qid || index}`}
                            sx={{ py: 1.25, borderTop: "1px solid #EEF0F4" }}
                          >
                            <Typography sx={{ color: "#535A70", fontSize: 12 }}>
                              {response.label || "Feedback response"}
                            </Typography>
                            <Typography sx={{ color: "#292D5B", fontSize: 13, fontWeight: 700, mt: 0.35 }}>
                              {formatAnswer(response.answer)}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

ParentFeedbackHistory.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

export default ParentFeedbackHistory;
